/**
 * Vue Plugin for Performance Monitoring
 * 
 * This plugin integrates performance monitoring into the Vue application.
 * It tracks route changes, component rendering, and other performance metrics.
 */

import type { App } from 'vue';
import type { Router } from 'vue-router';
import { initPerformanceMonitoring, markPerformance, measurePerformance } from '@/utils/performanceMonitor';

export interface PerformancePluginOptions {
  router?: Router;
  sampleRate?: number;
  reportToConsole?: boolean;
  reportToAnalytics?: boolean;
}

export default {
  install(app: App, options: PerformancePluginOptions = {}) {
    // Initialize performance monitoring
    initPerformanceMonitoring({
      sampleRate: options.sampleRate || 0.1,
      reportToConsole: options.reportToConsole !== undefined ? options.reportToConsole : true,
      reportToAnalytics: options.reportToAnalytics || false
    });
    
    // Track component lifecycle hooks if in development mode
    if (import.meta.env.DEV) {
      app.mixin({
        beforeCreate() {
          markPerformance(`${this.$options.name || 'anonymous'}-beforeCreate`);
        },
        mounted() {
          markPerformance(`${this.$options.name || 'anonymous'}-mounted`);
          measurePerformance(
            `${this.$options.name || 'anonymous'}-beforeCreate`,
            `${this.$options.name || 'anonymous'}-mounted`,
            `${this.$options.name || 'anonymous'}-renderTime`
          );
        }
      });
    }
    
    // Track router navigation if router is provided
    if (options.router) {
      options.router.beforeEach((to, from, next) => {
        markPerformance('routeNavigationStart', { from: from.path, to: to.path });
        next();
      });
      
      options.router.afterEach((to) => {
        markPerformance('routeNavigationEnd', { path: to.path });
        measurePerformance(
          'routeNavigationStart',
          'routeNavigationEnd',
          `navigation:${String(to.name) || 'unknown'}`
        );
      });
    }
    
    // Add global performance utility to the app
    app.config.globalProperties.$performance = {
      mark: markPerformance,
      measure: measurePerformance
    };
    
    // Provide performance utilities to components
    app.provide('performance', {
      mark: markPerformance,
      measure: measurePerformance
    });
  }
};
